import { useParams } from 'react-router-dom';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { fetchAccountAddresses } from 'api/account';
import { AddressForm } from 'components/address-form/AddressForm';
import {
    transformAddressFormToRequest,
    useAccountAddressForm,
} from 'components/address-form/useAccountAddressForm';
import { useDialogRoute } from 'components/dialog-route/DialogRouteContext';
import * as Drawer from 'components/drawer';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Address } from 'types/domain';

interface AddressFormDrawerProps {
    isMailingAddress?: boolean;
    onSuccess?: (newAddress: Address) => void;
}

const AddressFormDrawer = (props: AddressFormDrawerProps) => {
    const { isMailingAddress = false, onSuccess } = props;
    const { addressId } = useParams<{ addressId: string }>();
    const { accountId } = useCurrentAccount();
    const isEditing = !!addressId;
    const { goBack } = useDialogRoute();
    const { data: addresses } = useSuspenseQuery({
        queryKey: accountQueryKeys.addresses(accountId),
        queryFn: () => fetchAccountAddresses(accountId),
        select: data => data.data,
    });
    const showPrimary = !isEditing && isMailingAddress && (addresses ?? []).length > 0;

    const { methods, createOrUpdateMutation } = useAccountAddressForm({
        addressId: isEditing ? Number(addressId) : undefined,
        onSuccess: newAddress => {
            onSuccess?.(newAddress);
            goBack();
        },
        isMailingAddress,
    });
    const { handleSubmit, path, control } = methods;
    const { mutate, isPending } = createOrUpdateMutation;
    const submit = handleSubmit(values => {
        mutate(transformAddressFormToRequest(values));
    });

    return (
        <Form.GivebutterFormProvider {...methods}>
            <form className={'contents'} onSubmit={submit}>
                <Drawer.Header>
                    <Drawer.Title>
                        {isEditing ? 'Edit' : 'New'} {isMailingAddress && 'return'} address
                    </Drawer.Title>
                </Drawer.Header>
                <Drawer.Body className="flex flex-col gap-4">
                    {isMailingAddress && (
                        <Form.Container>
                            <Form.Label name={path('name')}>
                                Name
                                <Form.LabelAsterisk />
                            </Form.Label>
                            <Form.TextInputRoot
                                name={path('name')}
                                control={control}
                                rules={{ required: 'Name is required' }}
                            >
                                <Form.TextInputForm placeholder="Address name" />
                            </Form.TextInputRoot>
                        </Form.Container>
                    )}
                    <AddressForm isRequired={true} />
                    {showPrimary && (
                        <div className={'flex items-center gap-2'}>
                            <Form.UseFormCheckboxInput control={control} name={path(`isPrimary`)} />
                            <Form.Label name={path(`isPrimary`)}>Primary return address</Form.Label>
                        </div>
                    )}
                </Drawer.Body>
                <Drawer.Footer>
                    <div className={'flex w-full items-center justify-between gap-2'}>
                        <Drawer.Cancel asChild={true}>
                            <Button variant="transparent">Cancel</Button>
                        </Drawer.Cancel>

                        <Form.SubmitButton disabled={isPending}>
                            {isEditing ? 'Save' : 'Create'}
                        </Form.SubmitButton>
                    </div>
                </Drawer.Footer>
            </form>
        </Form.GivebutterFormProvider>
    );
};

export { AddressFormDrawer };
